import { Dialog, Transition } from '@headlessui/react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClipboardIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import React, { Fragment } from 'react';

import { convertSlackMarkdownToHtml } from '@/utils/slackMarkdownConverter';
import { MIN_DURATION_SECS } from '../../utils/constants';

const cleanText = (text: string) => text?.replace(/✅/g, '').trim() || '';

export function FeedbackSection({ title, children, icon: Icon }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg border border-gray-100 p-4 mb-4"
    >
      <div className="flex items-center mb-3">
        <Icon className="h-5 w-5 text-primary/80 mr-2" />
        <h3 className="text-base font-medium text-gray-900">{title}</h3>
      </div>
      {children}
    </motion.div>
  );
}

export function BulletList({ items }) {
  if (!items) {
    return (
      <ul className="mt-2 space-y-2">
        <li className="flex items-start">
          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/5 flex items-center justify-center mt-0.5 mr-2">
            <span className="text-primary/80 text-sm">•</span>
          </span>
          <span className="text-gray-600 text-sm leading-relaxed">
            No items
          </span>
        </li>
      </ul>
    );
  }
  return (
    <ul className="mt-2 space-y-2">
      {items?.map((item, idx) => (
        <motion.li
          key={idx}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: idx * 0.1 }}
          className="flex items-start"
        >
          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/5 flex items-center justify-center mt-0.5 mr-2">
            <span className="text-primary/80 text-sm">•</span>
          </span>
          <span
            className="text-gray-600 text-sm leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: convertSlackMarkdownToHtml(cleanText(item)),
            }}
          />
        </motion.li>
      ))}
    </ul>
  );
}

export function BulletList2({ items }) {
  console.log('::: BulletList -> items', items);

  if (!items) {
    return (
      <ul className="mt-2 space-y-2">
        <li className="flex items-start">
          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/5 flex items-center justify-center mt-0.5 mr-2">
            <span className="text-primary/80 text-sm">•</span>
          </span>
          <span className="text-gray-600 text-sm leading-relaxed">
            No items
          </span>
        </li>
      </ul>
    );
  }

  return (
    <ul className="mt-2 space-y-2">
      {items?.map((item, idx) => (
        <motion.li
          key={idx}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: idx * 0.1 }}
          className="flex items-start"
        >
          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/5 flex items-center justify-center mt-0.5 mr-2">
            <span className="text-primary/80 text-sm">•</span>
          </span>
          <span
            className="text-gray-600 text-sm leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: convertSlackMarkdownToHtml(cleanText(item.description)),
            }}
          />
        </motion.li>
      ))}
    </ul>
  );
}

function GeneratingFeedback() {
  return (
    <div className="flex flex-col items-center justify-center p-12 text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <SparklesIcon className="h-12 w-12 text-primary/80 mx-auto mb-4" />
        <motion.div
          className="h-1.5 w-24 bg-primary/10 rounded-full mx-auto overflow-hidden"
          initial={{ width: 0 }}
          animate={{ width: 96 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        >
          <div className="h-full w-1/2 bg-primary/60 rounded-full" />
        </motion.div>
      </motion.div>
      <h2 className="text-xl font-medium text-gray-900 mb-2">
        Analyzing your role-play session...
      </h2>
      <p className="text-gray-600 max-w-md mx-auto text-sm">
        We're preparing detailed feedback on your performance, communication
        style, and areas for improvement.
      </p>
    </div>
  );
}

export default function FeedbackModal({
  isOpen,
  onClose,
  feedback,
  duration,
  onRetry,
  isGenerating,
}: {
  isOpen: boolean;
  onClose: () => void;
  feedback?: any;
  duration: number;
  onRetry: () => void;
  isGenerating: boolean;
}) {
  const handleTryAgain = React.useCallback(() => {
    onClose();
    if (onRetry) {
      onRetry();
    }
  }, [onRetry, onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative transform rounded-xl bg-gray-50 w-full max-w-3xl shadow-2xl transition-all">
                {isGenerating ? (
                  <GeneratingFeedback />
                ) : duration < MIN_DURATION_SECS ? (
                  <div className="p-8 text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                      <ClipboardIcon
                        className="h-6 w-6 text-red-600"
                        aria-hidden="true"
                      />
                    </div>
                    <h2 className="text-xl font-medium text-gray-900 mb-4">
                      Session too short
                    </h2>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      {`Your conversation was too short to provide meaningful feedback. Try to practice for at least ${MIN_DURATION_SECS} seconds to get a proper assessment.`}
                    </p>
                    <button
                      onClick={handleTryAgain}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <ArrowPathIcon className="h-5 w-5 mr-2" />
                      Try again
                    </button>
                  </div>
                ) : feedback ? (
                  <>
                    <div className="flex items-center justify-between p-4 border-b border-gray-100">
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                        <Dialog.Title className="text-lg font-medium text-gray-900">
                          Practice session complete
                        </Dialog.Title>
                      </div>
                      <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 transition-colors"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>

                    <div className="max-h-[calc(100vh-200px)] overflow-y-auto p-6">
                      <FeedbackSection title="Overview" icon={CheckCircleIcon}>
                        <div
                          className="text-gray-600 text-sm"
                          dangerouslySetInnerHTML={{
                            __html: convertSlackMarkdownToHtml(
                              cleanText(feedback.scenarioOverview),
                            ),
                          }}
                        />
                      </FeedbackSection>

                      <FeedbackSection
                        title="Assessment"
                        icon={CheckCircleIcon}
                      >
                        <div
                          className="text-gray-600 text-sm"
                          dangerouslySetInnerHTML={{
                            __html: convertSlackMarkdownToHtml(
                              cleanText(feedback.objectiveAssessment),
                            ),
                          }}
                        />
                      </FeedbackSection>

                      <FeedbackSection title="Strengths" icon={CheckCircleIcon}>
                        <BulletList items={feedback.strengths} />
                      </FeedbackSection>

                      <FeedbackSection
                        title="Areas for improvement"
                        icon={CheckCircleIcon}
                      >
                        <BulletList items={feedback.areasForImprovement} />
                      </FeedbackSection>

                      <FeedbackSection
                        title="Next steps"
                        icon={CheckCircleIcon}
                      >
                        <BulletList2 items={feedback.actionableNextSteps} />
                      </FeedbackSection>
                    </div>

                    <div className="border-t border-gray-100 bg-white p-4 rounded-b-xl flex items-center gap-3">
                      <button
                        onClick={handleTryAgain}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <ArrowPathIcon className="h-4 w-4 mr-2" />
                        Try again
                      </button>
                      <div className="flex-1" />
                      <button
                        onClick={onClose}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Close
                      </button>
                    </div>
                  </>
                ) : null}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
